import { Box, Divider, Paper, Popover, styled, Typography } from '@mui/material';
import React from 'react';
import { AutomationRule } from '../rules.types';
import ReactJson from 'react-json-view';
import { addOpacity } from 'lib/helpers';
import { RelatedRulesLink } from './RelatedRulesLink';

interface EffectVisualisationData {
    errors?: string | string[];
    showErrors?: boolean;
    label: string;
    effect: AutomationRule['effect'];
    rulesLink?: string;
}

interface AutomationNodeEffectPopupProps {
    anchor: Element;
    onClose?: () => void;
    data: EffectVisualisationData;
}

const StyledCodeBackground = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor: addOpacity(theme.palette.text.primary, 0.05),
    padding: theme.spacing(1)
}));

const StyledTextButton = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    textDecoration: 'underline',
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    padding: theme.spacing(0, 1, 1, 1)
}));

const MainText = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    color: theme.palette.text.primary
}));

const SecondaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    opacity: 0.75,
    fontSize: '0.7rem'
}));

export const AutomationNodeEffectPopup: React.FC<AutomationNodeEffectPopupProps> = ({
    anchor,
    onClose,
    data
}) => {
    const [showCode, setShowCode] = React.useState(false);
    const handleShowCode = React.useCallback(() => {
        setShowCode(true);
    }, []);
    const handleHideCode = React.useCallback(() => {
        setShowCode(false);
    }, []);
    const errors = React.useMemo(
        () => (Array.isArray(data.errors) ? data.errors : [data.errors]),
        [data.errors]
    );

    return (
        <Popover
            open={!!anchor}
            anchorEl={anchor}
            aria-hidden="false"
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            disableRestoreFocus
            onClose={onClose}
        >
            <Paper elevation={3} sx={{ minWidth: 200, maxWidth: 500 }}>
                <Box p={0.5}>
                    <Typography variant="body2" align="center">
                        Effect
                    </Typography>
                </Box>
                <Divider />
                <Box p={1}>
                    <MainText sx={{ fontSize: '1rem' }} variant="body1">
                        {data.label}
                    </MainText>
                </Box>
                {data.showErrors && errors && (
                    <Box p={1}>
                        <SecondaryText variant="body2">Errors</SecondaryText>
                        {errors.map(error => (
                            <MainText
                                variant="body1"
                                key={error}
                                sx={{
                                    opacity: 0.8,
                                    color: 'error.main'
                                }}
                            >
                                {error}
                            </MainText>
                        ))}
                    </Box>
                )}
                {data.rulesLink && (
                    <Box p={1}>
                        <MainText>
                            <RelatedRulesLink to={data.rulesLink}>Show related automations</RelatedRulesLink>
                        </MainText>
                    </Box>
                )}
                {data.effect && (
                    <Box>
                        {!showCode && (
                            <StyledTextButton align="right" onClick={handleShowCode}>
                                Show effect code
                            </StyledTextButton>
                        )}
                        {showCode && (
                            <React.Fragment>
                                <StyledTextButton align="right" onClick={handleHideCode}>
                                    Hide effect code
                                </StyledTextButton>
                                <StyledCodeBackground>
                                    <ReactJson
                                        indentWidth={5}
                                        enableClipboard={false}
                                        name="effect"
                                        src={data.effect}
                                    />
                                </StyledCodeBackground>
                            </React.Fragment>
                        )}
                    </Box>
                )}
            </Paper>
        </Popover>
    );
};
